import { CloseCircleFilled, QuestionCircleTwoTone } from '@ant-design/icons';
import { faBrain, faFileAlt, faMoneyCheckAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Divider, Form, Input, message, Row, Select, Tooltip } from 'antd';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { TAInput, TAInputNumber, TAUpload } from '../../../components';
import constants from '../../../components/dashboard/constants';
import { SkillSelector } from '../../../components/dashboard/skill-selector';
import { TASelect, Title } from '../../../components/dashboard/styles';
import { DynamicFields } from '../../../components/dynamic-fields/dynamic-fields';
import { DynamicFormItem } from '../../../components/dynamic-form-item';
import { MultStepForm } from '../../../components/multi-step-form/multi-step-form';
import Currencies from '../../../constants/currencies';
import Languages from '../../../constants/languages';
import SkillsList from '../../../constants/skills';
import { getMaxExp } from '../../../utils/get-max-exp';
import { transformToFormData } from '../../../utils/transform-to-formdata';
import { useTheme } from 'styled-components';

const LanguagesList = Languages.map(l => l.name);

const { Option } = Select;

const { JobTypes, CareerLevels, ProficiencyLevels } = constants;

const jobTypes = Object.values(JobTypes);
const careerLevels = Object.values(CareerLevels);
const proficiencyLevels = Object.values(ProficiencyLevels);

const steps = ['Job Info', 'Package', 'Skills'];

export const JobForm: React.FC<any> = ({ job, onSubmit, onHiringProcessRemove }) => {
	const [projectLogo, setProjectLogo] = useState();
	const [errors, setErrors] = useState<any>([
		{
			'title': null,
			'careerLevel': null,
			'jobType': null,
			'jobDescription': null,
			'vacancies': null,
			'externalLinks': null,
			'yearsOfExp.min': null,
			'yearsOfExp.max': null
		},
		{
			'package.min': null,
			'package.max': null,
			'package.currency': null,
			'package.otherBenefits': null
		},
		{
			languages: null,
			keywords: null,
			hiringProcess: null,
			skills: null,
			project: null
		}
	]);
	const [validSteps, setValidSteps] = useState<any>([undefined, undefined, undefined]);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [skills, setSkills] = useState<any>([]);
	const [languages, setLanguages] = useState<any>([]);
	const [jobDescription, setJobDescription] = useState<any>(EditorState.createEmpty());
	const [hiringProcess, setHiringProcess] = useState<any>([]);

	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const [form] = Form.useForm();

	const editorStyles = {
		border: '1px solid #d9d9d9',
		height: '350px',
		lineHeight: '18px',
		paddingLeft: '5px',
		backgroundColor: theme.TA_LIGHT,
		resize: 'vertical',
		overflow: 'auto'
	};

	const mountLogic = () => {
		if (location && location.search) {
			const qs = new URLSearchParams(location.search);
			const step = qs.get('step');
			if (step) setCurrentStep(parseInt(step) - 1);
		}
	};

	useEffect(() => {
		mountLogic();
	}, [location?.search]);

	useEffect(() => {
		if (job) {
			let editorState;

			const content = htmlToDraft(job.jobDescription);
			if (content) {
				const contentState = ContentState.createFromBlockArray(content.contentBlocks);
				editorState = EditorState.createWithContent(contentState);
			}
			job.jobDescription = editorState;
			setJobDescription(job.jobDescription);

			if (job.externalLinks.length === 0) job.externalLinks = [''];
			setLanguages(job.languages);
			setSkills(job.skills);
			setHiringProcess(job.hiringProcess);

			form.setFieldsValue({
				...job
			});
		}
	}, [job]);

	const onNextStep = async (currentStep, nextStep, next) => {
		try {
			const fields = Object.keys(errors[currentStep]).map(k => k.split('.'));
			await form.validateFields(fields);
			validSteps.splice(currentStep, 1, true);
			setValidSteps([...validSteps]);
			next();
			navigate(window.location.pathname + `?step=${nextStep + 1}`);
		} catch (e: any) {
			e.errorFields.splice(currentStep, 1, errors);
			validSteps.splice(currentStep, 1, false);
			setErrors([...errors]);
			setValidSteps([...validSteps]);
			return next(false);
		}
	};

	const onPrevStep = (currentStep, prevStep, next) => {
		navigate(window.location.pathname + `?step=${prevStep + 1}`);
		return next();
	};

	const onSkillItemRemove = (item, indx, newSkills, oldSkills) => {
		setSkills(newSkills);
	};

	const onSkillItemAdd = (skill, newSkills, oldSkills) => {
		message.success(`New skill ${skill.name} added.`);
		setSkills(newSkills);
	};

	const onLanguageItemRemove = (item, indx, newLanguages, oldLanguages) => {
		setLanguages(newLanguages);
	};

	const onLanguageItemAdd = (language, newLanguages, oldLanguages) => {
		message.success(`New skill ${language.name} added.`);
		setLanguages(newLanguages);
	};

	const onFinish = async values => {
		if (skills?.length) values.skills = skills;
		if (languages?.length) values.languages = languages;
		if (values.externalLinks?.length) values.externalLinks = values.externalLinks.filter(link => link.length !== 0);
		if (!values.yearsOfExp.max) values.yearsOfExp.max = getMaxExp(values.yearsOfExp.min);
		let filteredHiringProcess = [];
		if (hiringProcess.length) {
			filteredHiringProcess = hiringProcess.filter(p => Object.keys(p).length > 0);
		}
		values.hiringProcess = filteredHiringProcess;
		values.jobDescription = draftToHtml(convertToRaw(jobDescription.getCurrentContent()));
		values.projectLogo = values.projectLogo?.file || null;
		for (let i = 0; i < hiringProcess.length; i += 1) {
			delete values[`processStep[${i}].title`];
			delete values[`processStep[${i}].description`];
		}

		const fd = transformToFormData(values);

		return await onSubmit(fd);
	};

	const getStepValidationState = step => {
		const stepState = validSteps[step];
		return stepState === false ? 'error' : stepState === true ? 'done' : 'wait';
	};

	const renderProcessStep: any = ({ value, onChange, ...props }) => {
		return (
			<>
				<Form.Item
					label={`Step ${props.index + 1} Title`}
					name={`processStep[${props.index}].title`}
					initialValue={value?.title}
					rules={[
						{ required: true, message: 'Please input the step title' },
						{
							validator(rule, value) {
								const str = value.trim();
								if (str) return Promise.resolve();
								return Promise.reject("Can't be empty string");
							}
						}
					]}
				>
					<TAInput
						placeholder="Name of the step"
						onChange={e => {
							const str = e.target.value.trim();
							if (str)
								setHiringProcess(
									hiringProcess.map((p, idx) => {
										if (idx === props.index) p = { ...p, title: str };
										return p;
									})
								);
						}}
					/>
				</Form.Item>
				<Form.Item
					label={`Step ${props.index + 1} Description`}
					name={`processStep[${props.index}].description`}
					initialValue={value?.description}
				>
					<Input.TextArea
						rows={6}
						placeholder="describe this step"
						onChange={e => {
							setHiringProcess(
								hiringProcess.map((p, idx) => {
									if (idx === props.index) p = { ...p, description: e.target.value };
									return p;
								})
							);
						}}
					/>
				</Form.Item>
			</>
		);
	};

	return (
		<MultStepForm
			form={form}
			current={currentStep}
			onFinish={onFinish}
			onNext={onNextStep}
			onPrev={onPrevStep}
			onMenueSelect={(currentStep, newStep, next) => {
				return newStep > currentStep
					? onNextStep(currentStep, newStep, next)
					: onPrevStep(currentStep, newStep, next);
			}}
			showMenu
		>
			<MultStepForm.Step
				title="What is your technical requirements?"
				status={getStepValidationState(0)}
				menuItem={
					<Title
						style={{ color: getStepValidationState(0) === 'error' && '#ff4d4f' }}
						icon={<FontAwesomeIcon icon={faFileAlt} style={{ opacity: '50%' }} className="me-1" />}
					>
						<span>{steps[0]}</span>
					</Title>
				}
			>
				<Form.Item
					label={<label className="ta-input-label">Job Title</label>}
					name={['title']}
					rules={[
						{
							required: true,
							message: 'please provide the job title.'
						}
					]}
				>
					<TAInput placeholder="Software engineer" />
				</Form.Item>

				<Row gutter={8}>
					<Col xs={12}>
						<Form.Item
							label={<label className="ta-input-label">Job Type</label>}
							name={['jobType']}
							rules={[
								{
									required: true,
									message: 'Please select a type.'
								}
							]}
						>
							<TASelect
								showSearch
								placeholder="Select the job type"
								optionFilterProp="children"
								filterOption={(input, option) =>
									option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
								mode="multiple"
								tokenSeparators={[',']}
							>
								{jobTypes.map(type => (
									<Option value={type} key={type}>
										{type}
									</Option>
								))}
							</TASelect>
						</Form.Item>
					</Col>

					<Col xs={12}>
						<Form.Item
							label={<label className="ta-input-label">Career Level</label>}
							name={['careerLevel']}
							rules={[
								{
									required: true,
									message: 'please provide the required career level.'
								}
							]}
						>
							<TASelect
								showSearch
								placeholder="Select the career level"
								optionFilterProp="children"
								filterOption={(input, option) =>
									option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
							>
								{careerLevels.map(type => (
									<Option value={type} key={type}>
										{type}
									</Option>
								))}
							</TASelect>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={8}>
					<Col xs={24}>
						<Form.Item
							label={<label className="ta-input-label">No. Vacancies</label>}
							name={['vacancies']}
							rules={[
								{
									required: true,
									message: 'You have to specify the number of vacancies'
								},
								{
									validator: (_, val) => (val > 0 ? Promise.resolve() : Promise.reject()),
									message: 'Not valid number of vacancies.'
								}
							]}
						>
							<TAInputNumber
								min={1}
								placeholder="Enter Number of open positions"
								formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={8}>
					<Col xs={24}>
						<label className="ta-input-label">Years of experience</label>

						<div className="d-flex justify-content-between">
							<Form.Item
								label={<label className="ta-input-label">Minimum</label>}
								wrapperCol={{ span: 23 }}
								className="d-inline-block w-100"
								name={['yearsOfExp', 'min']}
								rules={[
									{
										required: true,
										message: 'please provide the minimum required years of experience.'
									}
								]}
							>
								<TAInputNumber min={0} placeholder="0 for fresh grads" />
							</Form.Item>

							<Form.Item
								wrapperCol={{ span: 23, offset: 1 }}
								label={<label className="ta-input-label ms-3">Maximum</label>}
								className="d-inline-block w-100"
								name={['yearsOfExp', 'max']}
								rules={[
									{
										validator: (rule, value) => {
											const minExp = form.getFieldValue('yearsOfExp.min');
											if (value && +value < +minExp)
												return Promise.reject(
													"Maximum experience can't be less than minimum experience."
												);
											else return Promise.resolve();
										}
									}
								]}
							>
								<TAInputNumber min={0} placeholder="optional: max years of exp" />
							</Form.Item>
						</div>
					</Col>
				</Row>
				<DynamicFormItem
					label={<label className="ta-input-label ms-3">External Links</label>}
					btnText="Add External Link"
					itemName={['externalLinks']}
					placeholder="External Links"
					rules={[{ type: 'url', message: 'Not valid URL' }]}
				>
					<TAInput placeholder="e.g., http://www.xyz.com/post/4549-software-engineer" width="100%" />
				</DynamicFormItem>

				<Form.Item
					label={<label className="ta-input-label">Job Description</label>}
					name="jobDescription"
					rules={[
						{
							required: true,
							message: 'Please add job description.'
						}
					]}
				>
					<Editor
						toolbar={{
							options: [
								'inline',
								'blockType',
								'fontSize',
								'fontFamily',
								'list',
								'textAlign',
								'colorPicker',
								'link',
								'emoji'
							],
							inline: {
								inDropdown: true,
								className: undefined,
								dropdownClassName: 'comment-editor--inline'
								// options: ['bold', 'italic']
							},
							link: {
								popupClassName: 'comment-editor--link',
								showOpenOptionOnHover: false
							},
							list: {
								inDropdown: true,
								options: ['unordered', 'ordered'],
								dropdownClassName: 'comment-editor--list'
							},
							colorPicker: {
								popupClassName: 'comment-editor--colorPicker',
								colors: [
									'rgb(23, 43, 77)',
									'rgb(151, 160, 175)',
									'rgb(101, 84, 192)',
									'rgb(0, 184, 217)',
									'rgb(54, 179, 126)',
									'rgb(255, 86, 48)',
									'rgb(255, 153, 31)'
								],
								heighlight: undefined
							}
						}}
						editorClassName="comment-editor"
						onEditorStateChange={setJobDescription}
						editorStyle={editorStyles}
						editorState={jobDescription}
						toolbarStyle={{
							backgroundColor: 'rgba(168,197,224,0.2)',
							borderColor: '#A8C6DF'
						}}
						// ref={editorRef}
					/>
				</Form.Item>
				<Form.Item
					label={
						<label className="ta-input-label">
							Hiring Process{' '}
							<Tooltip title="This is usefull for better sourcing and matching" placement="top">
								<QuestionCircleTwoTone twoToneColor="#b2b2b2" />
							</Tooltip>
						</label>
					}
				>
					{job?.hiringProcess
						? job.hiringProcess.map((step, index) => {
								return (
									<Row gutter={24}>
										<Col xs={22}>
											{renderProcessStep({
												placeholder: '',
												style: { marginRight: 8 },
												value: step,
												index
											})}
										</Col>
										<Col xs={2}>
											<CloseCircleFilled
												style={{
													color: '#FF141E',
													fontSize: '19px',
													cursor: 'pointer',
													paddingTop: '40px'
												}}
												onClick={() => {
													onHiringProcessRemove(
														step._id,
														draftToHtml(convertToRaw(jobDescription.getCurrentContent()))
													);
												}}
											/>
										</Col>
									</Row>
								);
						  })
						: null}
					<DynamicFields
						min={hiringProcess.length}
						addBtnText={'Add step'}
						renderInput={renderProcessStep}
						preAdded={job?.hiringProcess?.length || 0}
						onAdd={() => setHiringProcess([...hiringProcess, {}])}
					/>
				</Form.Item>
			</MultStepForm.Step>
			<MultStepForm.Step
				title="Package Information"
				status={getStepValidationState(1)}
				menuItem={
					<Title
						style={{ color: getStepValidationState(1) === 'error' && '#ff4d4f' }}
						icon={<FontAwesomeIcon icon={faMoneyCheckAlt} style={{ opacity: '50%' }} className="me-1" />}
					>
						<span>{steps[1]}</span>
					</Title>
				}
			>
				<Row gutter={16}>
					<Col xs={{ span: 24 }}>
						<label className="ta-input-label">Salary Range: </label>
					</Col>
					<Col xs={{ span: 24 }} sm={{ span: 6 }}>
						<Form.Item
							label={<label className="ta-input-label">Currency</label>}
							name={['package', 'currency']}
							initialValue="EGP"
							rules={[
								{
									required: true,
									message: 'Please specify the currency.'
								}
							]}
						>
							<TASelect
								showSearch
								filterOption={(input, option) =>
									option.props.children.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
							>
								{Object.keys(Currencies).map(currency => (
									<Option value={currency} key={currency}>
										<Tooltip title={Currencies[currency]}>{currency}</Tooltip>
									</Option>
								))}
							</TASelect>
						</Form.Item>
					</Col>
					<Col xs={{ span: 24 }} sm={{ span: 9 }}>
						<Form.Item
							label={<label className="ta-input-label">from</label>}
							name={['package', 'min']}
							rules={[
								{
									required: true,
									message: 'you must specify the minimum salary.'
								},
								{
									validator: (_, val) => (val > 0 ? Promise.resolve() : Promise.reject()),
									message: 'Not a valid Salary, Be generous.'
								},
								{
									validator: (_, val) => {
										// validateFields(['package.max']);
										return form.getFieldValue(['package', 'max']) && val
											? val < form.getFieldValue(['package', 'max'])
												? Promise.resolve()
												: Promise.reject()
											: Promise.resolve();
									},
									message: 'Not valid Range, Must be less than maximum.'
								}
							]}
						>
							<TAInputNumber
								min={0}
								formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={value => `${value}`.replace(/\s?(,*)\s?/g, '')}
								placeholder="from, e.g., 6000"
								step={100}
							/>
						</Form.Item>
					</Col>

					<Col xs={{ span: 24 }} sm={{ span: 9 }}>
						<Form.Item
							label={<label className="ta-input-label">to</label>}
							name={['package', 'max']}
							rules={[
								{
									validator: (_, val) => {
										return form.getFieldValue(['package', 'min']) && val
											? val > form.getFieldValue(['package', 'min'])
												? Promise.resolve()
												: Promise.reject()
											: Promise.resolve();
									},
									message: 'Not valid Range, Must be greater than minimum.'
								}
							]}
						>
							<TAInputNumber
								min={0}
								formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={value => `${value}`.replace(/\s?(,*)\s?/g, '')}
								placeholder="to, e.g., 8000"
								step={100}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					label={<label className="ta-input-label">Other benefits</label>}
					name={['package', 'otherBenefits']}
					rules={[{ max: 10000, message: `maximum 10000 characters` }]}
				>
					<Input.TextArea rows={6} placeholder="e.g, 2 days work from home per month" />
				</Form.Item>
			</MultStepForm.Step>
			<MultStepForm.Step
				title="Skills Information"
				status={getStepValidationState(2)}
				menuItem={
					<Title
						style={{ color: getStepValidationState(2) === 'error' && '#ff4d4f' }}
						icon={<FontAwesomeIcon icon={faBrain} style={{ opacity: '50%' }} className="me-1" />}
					>
						<span>{steps[2]}</span>
					</Title>
				}
			>
				<SkillSelector
					title="Skills"
					className="mb-3"
					dataSource={skills}
					onItemRemove={onSkillItemRemove}
					onItemAdd={onSkillItemAdd}
					proficiencyLevels={proficiencyLevels}
					skillForm={{
						labels: {
							skillName: 'Technology'
						},
						placeholders: {
							skillName: 'Enter the technology name, e.g. Java'
						}
					}}
					skillsOptions={SkillsList}
					addBtnText="Add Skill"
					emptyListText="No Skills"
					showPlus
				/>

				<Divider />

				<SkillSelector
					title="Languages"
					dataSource={languages}
					onItemRemove={onLanguageItemRemove}
					onItemAdd={onLanguageItemAdd}
					proficiencyLevels={proficiencyLevels}
					skillForm={{
						labels: {
							skillName: 'Language'
						},
						placeholders: {
							skillName: 'Enter the language name, e.g. English'
						}
					}}
					skillsOptions={LanguagesList}
					addBtnText="Add Language"
					emptyListText="No Languages"
					showPlus
					skillModalFormTitle="New Language"
				/>

				<Form.Item
					label={
						<label className="ta-input-label">
							Keywords{' '}
							<Tooltip title="this is usefull for better search result and indexing." placement="top">
								<QuestionCircleTwoTone twoToneColor="#b2b2b2" />
							</Tooltip>
						</label>
					}
					name="keywords"
				>
					<TASelect
						mode="tags"
						tokenSeparators={[',']}
						placeholder="e.g., DevOps, IT"
						dropdownClassName="d-none"
					></TASelect>
				</Form.Item>

				<Form.Item label={<label className="ta-input-label">Project</label>} name="project">
					<TAInput placeholder="project name" />
				</Form.Item>
				<Form.Item
					name="projectLogo"
					label="Project Logo"
					rules={[
						{
							validator: (_, value, cb) => {
								value && setProjectLogo(value.fileList);
								cb();
							}
						}
					]}
				>
					<TAUpload
						accept=".png,.jpg,.jpeg"
						beforeUpload={() => false}
						btnText="Add Image"
						listType="picture-card"
						maxCount={1}
						multiple
						fileList={projectLogo}
					/>
				</Form.Item>
			</MultStepForm.Step>
		</MultStepForm>
	);
};
